import {Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {Priority} from '@app/layout/rules/template-editor/api/template';
import {TemplatesService} from '@app/layout/rules/template-editor/services/templates.service';
import {finalize, takeUntil} from 'rxjs/operators';
import {I18nMessage} from '@shared/api/model/i18n';
import {updateControlEnableState} from '@app/shared/util/angular/angular-utils';

@Component({
  selector: 'app-priority-select',
  templateUrl: './priority-select.component.html',
  styleUrl: './priority-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PrioritySelectComponent),
      multi: true
    }
  ],
  standalone: false
})
export class PrioritySelectComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor {
  @Input() appendTo: string;
  @Input() priorities: Priority[];
  @Input() isReadOnly: boolean;
  @Input() loading = false;
  @Input() placeholder: string;
  @Input() multiSelect = false;
  @Input() clearable = true;
  @Input() maxSelectedLabels: number;
  @Input() maxSelectedShowItemName = true;
  @Input() truncateLabels = true;
  @Input() maxSelectedItemName: I18nMessage;
  @Output() prioritiesLoaded = new EventEmitter<Priority[]>();
  @Output() openEvent = new EventEmitter<any>();
  @Output() closeEvent = new EventEmitter<any>();

  private readonly ngUnsubscribe = new Subject<void>();

  private fetchPrioritiesSubscription: Subscription;

  formControl = new FormControl<Priority | Priority[]>(null);

  onChange: (_) => void = (_) => {};
  onTouched: () => void = () => {};

  constructor(private templatesService: TemplatesService) {}

  ngOnInit() {
    this.formControl.valueChanges.pipe(takeUntil(this.ngUnsubscribe)).subscribe((value: Priority | Priority[]) => this.onChange(value));
    if (this.priorities == null) {
      this.fetchPriorities();
    }
  }

  ngOnChanges(changes) {
    if (changes.priorities) {
      if (this.priorities) {
        this.cancelSubscription();
      } else {
        this.fetchPriorities();
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.cancelSubscription();
  }

  private fetchPriorities() {
    if (this.fetchPrioritiesSubscription == null) {
      this.loading = true;
      this.fetchPrioritiesSubscription = this.templatesService.getPriorities()
        .pipe(finalize(() => {
          this.loading = false;
          this.cancelSubscription();
        })).subscribe((priorities: Priority[]) => {
          this.priorities = priorities;
          this.prioritiesLoaded.emit(priorities);
        });
    }
  }

  private cancelSubscription() {
    if (this.fetchPrioritiesSubscription != null) {
      this.fetchPrioritiesSubscription.unsubscribe();
      this.fetchPrioritiesSubscription = null;
    }
  }

  writeValue(value: Priority | Priority[]): void {
    this.formControl.setValue(value, {emitEvent: false});
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onOpen() {
    this.openEvent.emit();
  }

  onClose() {
    this.closeEvent.emit();
  }

  setDisabledState(isDisabled: boolean): void {
    updateControlEnableState(this.formControl, !isDisabled);
  }
}
